import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { openDillToast } from '../../../../../../utils/helpers';
import { Supplier, StatementSchedule, StatementScheduleFrequency } from '@dill/dill-shared';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { DillButton, DillToggle, DillSelect, DillInput } from '../../../../../general/Dill';

interface StatementsSettings {
  isAutoStatementsDeactivated: boolean;
  isManualStatementsDeactivated: boolean;
  isAdminEmailSender: boolean;
  includeAttachments: boolean;
  automatedStatementNotesTemplate: string;
}

// Flat editing draft for the schedule. We keep every possible field here and assemble the
// proper discriminated-union value on save based on the selected frequency.
interface ScheduleDraft {
  frequency: '' | StatementScheduleFrequency;
  dayOfMonth: string;
  businessDay: string;
  weekday: string;
}

interface StatementsSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialSettings = (supplier?: Supplier | null): StatementsSettings => ({
  isAutoStatementsDeactivated:
    supplier?.invoiceStatementsSettings?.isAutoStatementsDeactivated ?? false,
  isManualStatementsDeactivated:
    supplier?.invoiceStatementsSettings?.isManualStatementsDeactivated ?? false,
  isAdminEmailSender: supplier?.invoiceStatementsSettings?.isAdminEmailSender ?? false,
  includeAttachments: supplier?.invoiceStatementsSettings?.includeAttachments ?? false,
  automatedStatementNotesTemplate:
    supplier?.invoiceStatementsSettings?.automatedStatementNotesTemplate ?? '',
});

const getInitialSchedule = (supplier?: Supplier | null): ScheduleDraft => {
  const s = supplier?.invoiceStatementsSettings?.statementSchedule;
  return {
    frequency: s?.frequency ?? '',
    dayOfMonth: s?.frequency === 'DAY_OF_MONTH' ? String(s.dayOfMonth) : '',
    businessDay: s?.frequency === 'NTH_BUSINESS_DAY' ? String(s.businessDay) : '',
    weekday: s?.frequency === 'FIRST_WEEKDAY_OF_MONTH' ? String(s.weekday) : '',
  };
};

// Builds the discriminated-union schedule from the draft. Returns the schedule to persist
// (null when no frequency is configured) and whether the current draft is valid.
const buildSchedule = (
  draft: ScheduleDraft
): { schedule: StatementSchedule | null; valid: boolean } => {
  switch (draft.frequency) {
    case 'DAY_OF_MONTH': {
      const n = parseInt(draft.dayOfMonth, 10);
      if (!Number.isInteger(n) || n < 1 || n > 31) return { schedule: null, valid: false };
      return { schedule: { frequency: 'DAY_OF_MONTH', dayOfMonth: n }, valid: true };
    }
    case 'NTH_BUSINESS_DAY': {
      const n = parseInt(draft.businessDay, 10);
      if (!Number.isInteger(n) || n < 1 || n > 23) return { schedule: null, valid: false };
      return { schedule: { frequency: 'NTH_BUSINESS_DAY', businessDay: n }, valid: true };
    }
    case 'LAST_BUSINESS_DAY':
      return { schedule: { frequency: 'LAST_BUSINESS_DAY' }, valid: true };
    case 'LAST_DAY_OF_MONTH':
      return { schedule: { frequency: 'LAST_DAY_OF_MONTH' }, valid: true };
    case 'FIRST_WEEKDAY_OF_MONTH': {
      const wd = parseInt(draft.weekday, 10);
      if (!Number.isInteger(wd) || wd < 0 || wd > 6) return { schedule: null, valid: false };
      return {
        schedule: { frequency: 'FIRST_WEEKDAY_OF_MONTH', weekday: wd },
        valid: true,
      };
    }
    default:
      // No frequency selected = no auto schedule. Valid, persisted as null.
      return { schedule: null, valid: true };
  }
};

const FREQUENCY_OPTIONS: { label: string; value: '' | StatementScheduleFrequency }[] = [
  { label: 'Not scheduled', value: '' },
  { label: 'Day of month', value: 'DAY_OF_MONTH' },
  { label: 'Nth business day', value: 'NTH_BUSINESS_DAY' },
  { label: 'Last business day', value: 'LAST_BUSINESS_DAY' },
  { label: 'Last day of month', value: 'LAST_DAY_OF_MONTH' },
  { label: 'First chosen day of week of month', value: 'FIRST_WEEKDAY_OF_MONTH' },
];

const WEEKDAY_OPTIONS = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
];

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Plain-English summary of what the current draft will do, shown under the controls.
const describeSchedule = (draft: ScheduleDraft): string => {
  const { schedule, valid } = buildSchedule(draft);
  if (!valid) return 'Enter a valid value to complete the schedule.';
  if (!schedule) return 'Automatic statements will not be sent on a schedule.';
  switch (schedule.frequency) {
    case 'DAY_OF_MONTH':
      return `Sends on the ${ordinal(
        schedule.dayOfMonth
      )} of each month (the last day when that day doesn't exist).`;
    case 'NTH_BUSINESS_DAY':
      return `Sends on the ${ordinal(schedule.businessDay)} business day of each month.`;
    case 'LAST_BUSINESS_DAY':
      return 'Sends on the last business day of each month.';
    case 'LAST_DAY_OF_MONTH':
      return 'Sends on the last calendar day of each month (including weekends).';
    case 'FIRST_WEEKDAY_OF_MONTH': {
      const day = WEEKDAY_OPTIONS.find((w) => w.value === String(schedule.weekday))?.label;
      return `Sends on the first ${day} of each month.`;
    }
    default:
      return '';
  }
};

const TOGGLE_ROWS: {
  key: keyof StatementsSettings;
  label: string;
  description: string;
  inverted?: boolean;
}[] = [
  {
    key: 'isManualStatementsDeactivated',
    label: 'Manual Statements',
    description: 'Allow users to manually send statement emails to customers.',
    inverted: true,
  },
  {
    key: 'includeAttachments',
    label: 'Include Attachments',
    description: 'Attach statement PDFs to outgoing statement emails.',
  },
  {
    key: 'isAutoStatementsDeactivated',
    label: 'Automatic Statement Emails',
    description: 'Send statement emails automatically on a scheduled basis.',
    inverted: true,
  },
];

export const StatementsSettingsSection = ({ supplier }: StatementsSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [settings, setSettings] = useState<StatementsSettings>(() => getInitialSettings(supplier));
  const [initialSettings, setInitialSettings] = useState<StatementsSettings>(() =>
    getInitialSettings(supplier)
  );
  const [schedule, setSchedule] = useState<ScheduleDraft>(() => getInitialSchedule(supplier));
  const [initialSchedule, setInitialSchedule] = useState<ScheduleDraft>(() =>
    getInitialSchedule(supplier)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const newSettings = getInitialSettings(supplier);
    setSettings(newSettings);
    setInitialSettings(newSettings);
    const newSchedule = getInitialSchedule(supplier);
    setSchedule(newSchedule);
    setInitialSchedule(newSchedule);
  }, [supplier?.id]);

  const settingsChanged = useMemo(
    () =>
      settings.isAutoStatementsDeactivated !== initialSettings.isAutoStatementsDeactivated ||
      settings.isManualStatementsDeactivated !== initialSettings.isManualStatementsDeactivated ||
      settings.isAdminEmailSender !== initialSettings.isAdminEmailSender ||
      settings.includeAttachments !== initialSettings.includeAttachments ||
      settings.automatedStatementNotesTemplate !== initialSettings.automatedStatementNotesTemplate,
    [settings, initialSettings]
  );

  const scheduleChanged = useMemo(
    () => JSON.stringify(schedule) !== JSON.stringify(initialSchedule),
    [schedule, initialSchedule]
  );

  const scheduleValid = useMemo(() => buildSchedule(schedule).valid, [schedule]);

  const handleSave = async () => {
    const { schedule: statementSchedule, valid } = buildSchedule(schedule);
    if (!valid) {
      openDillToast({ type: 'ERROR', message: 'Please complete the statement schedule' });
      return;
    }
    setSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id || '',
        supplierDetails: {
          // Spread existing settings so we don't drop other persisted fields
          // (e.g. snapshot settings, savedStatementMonths).
          invoiceStatementsSettings: {
            ...supplier?.invoiceStatementsSettings,
            ...settings,
            statementSchedule,
          },
        },
      })
    );
    setSaving(false);

    if (result.type === 'supplier/update/fulfilled') {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Statements settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save statements settings' });
    }
  };

  const isStatementSnapshotEnabled =
    !!supplier?.invoiceStatementsSettings?.isStatementSnapshotEnabled;

  const updateSchedule = (patch: Partial<ScheduleDraft>) =>
    setSchedule((prev) => ({ ...prev, ...patch }));

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Statements</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Configure how statement emails are sent to customers.
        </p>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        {TOGGLE_ROWS.map((row, i) => {
          const isAutoToggleDisabledBySnapshot =
            row.key === 'isAutoStatementsDeactivated' && isStatementSnapshotEnabled;
          const tooltipId = isAutoToggleDisabledBySnapshot
            ? 'snapshot-auto-tooltip'
            : !canEdit
            ? 'no-permission-tooltip'
            : undefined;
          return (
            <label
              key={row.key}
              className={`flex items-center justify-between gap-4 py-3 cursor-pointer ${
                i < TOGGLE_ROWS.length - 1 ? 'border-b border-gray-100' : ''
              }`}>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800">{row.label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{row.description}</p>
              </div>
              <div data-tooltip-id={tooltipId}>
                <DillToggle
                  value={row.inverted ? !settings[row.key] : !!settings[row.key]}
                  onChange={() => setSettings((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                  isDisabled={!canEdit || isAutoToggleDisabledBySnapshot}
                />
              </div>
            </label>
          );
        })}

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-800">Custom notes</p>
          <p className="text-[11px] text-gray-400 mt-0.5 mb-3">
            Shown in the body of every automated statement email until changed. Can be updated at
            any time, independent of the billing month.
          </p>
          <textarea
            value={settings.automatedStatementNotesTemplate}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                automatedStatementNotesTemplate: e.target.value,
              }))
            }
            placeholder="Add a note to include on automated statement emails..."
            rows={3}
            disabled={!canEdit}
            className="w-full border border-gray-300 rounded-lg bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 resize-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-800">Statement schedule</p>
          <p className="text-[11px] text-gray-400 mt-0.5 mb-3">
            When automatic statement emails are sent each month.
          </p>

          <div className="flex flex-col gap-3">
            <div className="max-w-[300px]">
              <DillSelect
                value={schedule.frequency}
                onChange={(v) => updateSchedule({ frequency: v as ScheduleDraft['frequency'] })}
                options={FREQUENCY_OPTIONS}
                disabled={!canEdit}
              />
            </div>

            {schedule.frequency === 'DAY_OF_MONTH' && (
              <div className="max-w-[120px]">
                <DillInput
                  type="number"
                  value={schedule.dayOfMonth}
                  onChange={(v) => updateSchedule({ dayOfMonth: v })}
                  placeholder="Day (1-31)"
                  disabled={!canEdit}
                />
              </div>
            )}

            {schedule.frequency === 'NTH_BUSINESS_DAY' && (
              <div className="max-w-[160px]">
                <DillInput
                  type="number"
                  value={schedule.businessDay}
                  onChange={(v) => updateSchedule({ businessDay: v })}
                  placeholder="Business day #"
                  disabled={!canEdit}
                />
              </div>
            )}

            {schedule.frequency === 'FIRST_WEEKDAY_OF_MONTH' && (
              <div className="w-[150px]">
                <DillSelect
                  value={schedule.weekday}
                  onChange={(v) => updateSchedule({ weekday: v })}
                  options={WEEKDAY_OPTIONS}
                  placeholder="Weekday"
                  disabled={!canEdit}
                />
              </div>
            )}

            <p className="text-[11px] text-gray-500">{describeSchedule(schedule)}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || (!settingsChanged && !scheduleChanged) || !scheduleValid}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
      {isStatementSnapshotEnabled && (
        <ReactTooltip
          id="snapshot-auto-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          Automatic statements cannot be disabled while Statement Snapshot is enabled.
        </ReactTooltip>
      )}
    </div>
  );
};
